import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Spacing, fontFamily } from '@/shared/theme';
import { Text, CenteredColumn } from '@/shared/ui';
import ScaleOption from '@/shared/ScaleOption';
import { QuestionProgress } from '@/components/QuestionProgress';
import { BorisScreener } from '@/shared/borisScreenerTheme';
import { StartOverModal } from '@/components/StartOverModal';
import { CrisisResourceBlock } from '@/components/CrisisResourceBlock';
import { useRegistration } from '@/context/RegistrationContext';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useAppFlow, STEP_KEYS } from '@/hooks/useAppFlow';
import { PHQ9_QUESTION_COUNT, PHQ9_Q9_INDEX, sumAnswers, isCrisisResponse } from '@/utils/bhScreener';
import { VIEW_PHQ9_SCREENER, PHQ9_SCREENER_COMPLETED } from '@/consts/events';

// 4-point PHQ-9 answer scale (labels come from i18n phq9.a0-a3). No default selection.
const ANSWER_SCALE = [0, 1, 2, 3];

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: ${Spacing.x3}px;
  position: relative;
  z-index: 1;
`;

// Left-aligned content column, matching Boris's mobile composition.
const Content = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
`;

// Faint monospace question counter (Boris's rg-qcount).
const QuestionCount = styled.div`
  font-family: ui-monospace, 'SF Mono', Menlo, monospace;
  font-size: 12px;
  letter-spacing: 0.08em;
  /* inkFaint fails WCAG AA (2.76:1) for this real content; inkSoft passes (5.76:1). */
  color: ${BorisScreener.inkSoft};
  margin-bottom: ${Spacing.x1}px;
`;

// Small muted prompt (Boris's rg-sub) — replaces the large centered heading.
const SubHeading = styled.div`
  font-family: ${fontFamily};
  font-size: 16px;
  line-height: 1.35;
  color: ${BorisScreener.inkSoft};
`;

const QuestionText = styled(Text)`
  margin-top: ${Spacing.x1_5}px;
  font-size: 22px;
  font-weight: 500;
  color: ${BorisScreener.ink};

  /* Boris .reg-dt .rg-scale-q scales the question up on desktop. */
  @media (min-width: 900px) {
    font-size: 26px;
  }
`;

const OptionsList = styled.div`
  width: 100%;
  max-width: 560px;
  margin-top: ${Spacing.x2}px;
  display: flex;
  flex-direction: column;
  gap: ${Spacing.x1}px;
`;

/**
 * RND-35692 — PHQ-9 depression screener. Presents the 9 CMS-worded items one at
 * a time on a 0-3 scale, sums the score (client-side only, never submitted), and
 * routes to the GAD-7 screener. If Q9 (suicidality) is endorsed (>= 1) it shows
 * the non-blocking crisis-resource block before continuing.
 *
 * The score is NOT shown to the user and NOT emitted as an analytics property.
 */
const PHQ9ScreenerStep: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { updateBHScreener } = useRegistration();
  const { trackView, track } = useAnalytics();
  const [showStartOverModal, setShowStartOverModal] = useState(false);
  const { resetBlocker } = useAppFlow(STEP_KEYS.PHQ9_SCREENER, {
    onBackNavigationBlocked: () => setShowStartOverModal(true),
  });

  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<(number | null)[]>(() => Array(PHQ9_QUESTION_COUNT).fill(null));
  const [showCrisis, setShowCrisis] = useState(false);

  useEffect(() => {
    trackView({ event: VIEW_PHQ9_SCREENER });
  }, [trackView]);

  const handlePick = (value: string) => {
    const nextAnswers = answers.slice();
    nextAnswers[index] = Number(value);
    setAnswers(nextAnswers);

    if (index + 1 < PHQ9_QUESTION_COUNT) {
      setIndex(index + 1);
      return;
    }

    // Last question answered — finalize PHQ-9.
    const completeAnswers = nextAnswers as number[];
    const phq9Score = sumAnswers(completeAnswers);
    const crisis = isCrisisResponse(completeAnswers[PHQ9_Q9_INDEX]);

    // Client-side only — raw answers/score are never submitted (RND-35692).
    updateBHScreener({ phq9Answers: completeAnswers, phq9Score, crisisGateShown: crisis });

    // PHI: no score, no crisis flag, no per-question signal of any kind is emitted —
    // even the crisis-gate-shown boolean is a near-direct proxy for a Q9 suicidality
    // response, since the gate only shows for that specific trigger.
    // TODO(RND-35692): restore `score` prop once PHI review clears (Daniel).
    track({ event: PHQ9_SCREENER_COMPLETED });

    if (crisis) {
      setShowCrisis(true);
      return;
    }

    navigate('/gad7-screener');
  };

  const handleStartOver = () => {
    setShowStartOverModal(false);
    navigate('/');
  };

  const startOverModal = (
    <StartOverModal
      isOpen={showStartOverModal}
      onConfirm={handleStartOver}
      onCancel={() => {
        setShowStartOverModal(false);
        resetBlocker();
      }}
    />
  );

  // PHQ-9 Q9 crisis gate — non-blocking acknowledge before continuing to GAD-7.
  if (showCrisis) {
    return (
      <>
        <CrisisResourceBlock onContinue={() => navigate('/gad7-screener')} />
        {startOverModal}
      </>
    );
  }

  return (
    <Container>
      <CenteredColumn span={8}>
        <Content>
          <QuestionProgress total={PHQ9_QUESTION_COUNT} current={index} />
          <QuestionCount>
            {t('phq9.question-count', { current: index + 1, total: PHQ9_QUESTION_COUNT })}
          </QuestionCount>
          <SubHeading>{t('phq9.heading')}</SubHeading>
          <QuestionText size="p1">
            {t(`phq9.q${index + 1}`)}
          </QuestionText>

          {/* WCAG: single-select group; one PHQ-9 item at a time, no default. */}
          <OptionsList role="radiogroup" aria-label={t(`phq9.q${index + 1}`)}>
            {ANSWER_SCALE.map((value) => (
              <ScaleOption
                key={value}
                value={String(value)}
                label={t(`phq9.a${value}`)}
                selected={answers[index] === value}
                onClick={handlePick}
              />
            ))}
          </OptionsList>
        </Content>
      </CenteredColumn>

      {startOverModal}
    </Container>
  );
};

export { PHQ9ScreenerStep };
